/* eslint-disable react/jsx-no-target-blank */
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet';
import { authApi } from 'api';
import { useGlobal } from 'contexts/globalContext';
import { Button } from 'components/_new/Button';
import TextField from 'components/_common/forms/TextField';
import Checkbox from 'components/_common/forms/Checkbox';
import { colors } from 'theme';
import ValidatePassword from 'components/_common/forms/ValidatePassword';
import { classNames } from 'primereact/utils';
import { translatedStaticResourceResolver } from 'utils/translatedStaticResourceResolver';
import { AxiosError } from 'axios';
import { generateAutocompleteId } from 'utils/generate-autocomplete-id';
import LanguageSwitcher from './LanguageSwitcher';

interface RegisterOrganizationFormObject {
	email: string;
	password: string;
	confirmPassword: string;

	firstName: string;
	lastName: string;
	phoneNumber: string;

	billingCompanyName: string;
	billingTin: string;
	billingAddressStreetAndNumber: string;
	billingPostCode: string;
	billingCity: string;
	billingEmail: string;
	organizationName: string;

	terms: string;
}

const RegisterOrganization: FC = () => {
	const { i18n, t } = useTranslation();
	const { toastRef } = useGlobal();

	const [step, setStep] = useState(1);

	const {
		trigger,
		control,
		handleSubmit,
		watch,
		setError,
		formState: { errors },
	} = useForm<RegisterOrganizationFormObject>({
		defaultValues: {},
	});

	const [afterSend, setAfterSend] = useState(false);

	const {
		mutate,
		isSuccess,
		isLoading: isSubmitting,
	} = useMutation(authApi.registerOrganization, {
		onError: (error) => {
			if (error instanceof AxiosError) {
				const data = error.response?.data;
				const message = data?.message;
				if (message) {
					if (message === 'userEmailExists') {
						const translated = t(`auth.messages.${message}`);
						setError('email', {
							type: 'custom',
							message: translated,
						});
					} else {
						toastRef?.current?.show({
							severity: 'error',
							life: 3000,
							summary: t('misc.error'),
							detail: data.message,
						});
					}
				} else {
					toastRef?.current?.show({
						severity: 'error',
						life: 3000,
						summary: t('misc.error'),
						detail: error.message,
					});
				}
			}
		},
		onSuccess: () => {
			setAfterSend(true);
		},
	});

	if (isSuccess) {
		return (
			<>
				<div>{t('auth.thankYouMessage')}</div>
				<p>{t('auth.closeTabAndCheckInbox')}</p>
			</>
		);
	}

	const handleClickNext = () => {
		if (step === 1) {
			trigger(['email', 'password', 'confirmPassword', 'firstName', 'lastName', 'phoneNumber']).then(
				(valid: any) => {
					if (valid) {
						setStep(2);
					}
				}
			);
		}
	};

	const handleSubmitForm = handleSubmit(({ terms, confirmPassword, ...values }) => {
		mutate({ ...values, language: i18n.language });
	});

	const password = watch('password');

	return (
		<>
			<Helmet title={t('auth.register')} />
			{afterSend ? (
				<>
					<div>{t('auth.thankYouMessage')}</div>
					<p>{t('auth.closeTabAndCheckInbox')}</p>
				</>
			) : (
				<form onSubmit={handleSubmitForm} className="flex flex-column row-gap-4">
					<div className="text-sm">
						<p>{t('auth.registerTooltip1')}</p>
					</div>
					<div
						className={classNames('flex-column row-gap-4', {
							flex: step === 1,
							hidden: step !== 1,
						})}
					>
						<TextField
							name="email"
							placeholder={t('auth.inputs.email')}
							control={control}
							required
							autoComplete={generateAutocompleteId('login')}
						/>

						<ValidatePassword
							name="password"
							placeholder={t('auth.inputs.password')}
							control={control}
							required
							autoComplete={generateAutocompleteId('password')}
							rules={{
								minLength: { value: 10, message: t('auth.passwordMinLength') },
								maxLength: { value: 36, message: t('auth.passwordMaxLength') },
								validate: {
									customRequired: () => {
										const regex =
											/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!#%*?&])[A-Za-z\d@$#!%*?&]{10,36}$/;
										if (!regex.test(watch('password'))) {
											if (!/(?=.*[a-z])/.test(watch('password'))) {
												return t('auth.passwordRequiresLowercase');
											}

											if (!/(?=.*[A-Z])/.test(watch('password'))) {
												return t('auth.passwordRequiresUppercase');
											}

											if (!/(?=.*\d)/.test(watch('password'))) {
												return t('auth.passwordRequiresDigit');
											}

											if (!/(?=.*[@$#!%*?&])/.test(watch('password'))) {
												return t('auth.passwordRequiresSpecialChar');
											}
										}
										return true;
									},
								},
							}}
						/>

						<ValidatePassword
							name="confirmPassword"
							placeholder={t('auth.inputs.confirmPassword')}
							control={control}
							required
							rules={{
								validate: {
									matching: (v) => v === password || t('misc.forms.notMatchingPassword'),
								},
							}}
							autoComplete={generateAutocompleteId('password')}
						/>

						<TextField
							name="firstName"
							placeholder={t('auth.inputs.firstName')}
							control={control}
							required
						/>

						<TextField name="lastName" placeholder={t('auth.inputs.lastName')} control={control} required />

						<TextField
							name="phoneNumber"
							placeholder={t('auth.inputs.phoneNumber')}
							control={control}
							required
						/>

						<div className="mt-2">
							<Button label={t('auth.buttons.next')} onClick={handleClickNext} className="w-full" />
						</div>
					</div>

					<div
						className={classNames('flex-column row-gap-4', {
							flex: step === 2,
							hidden: step !== 2,
						})}
					>
						<div className="p-mb-3">
							<Button
								onClick={() => {
									setStep(1);
								}}
								label={t('auth.buttons.prev')}
								variant="primary-outlined"
								className="w-full"
							/>
						</div>

						<TextField
							name="billingCompanyName"
							placeholder={t('auth.inputs.billingCompanyName')}
							control={control}
							required
						/>

						<TextField
							name="billingTin"
							placeholder={t('auth.inputs.billingTin')}
							control={control}
							required
						/>

						<TextField
							name="billingAddressStreetAndNumber"
							placeholder={t('auth.inputs.billingAddressStreetAndNumber')}
							control={control}
							required
						/>

						<TextField
							name="billingPostCode"
							placeholder={t('auth.inputs.billingPostCode')}
							control={control}
							required
						/>

						<TextField
							name="billingCity"
							placeholder={t('auth.inputs.billingCity')}
							control={control}
							required
						/>

						<TextField
							name="billingEmail"
							placeholder={t('auth.inputs.billingEmail')}
							control={control}
							required
						/>

						<TextField
							name="organizationName"
							placeholder={t('auth.inputs.organizationName')}
							control={control}
							required
						/>

						<div className="mt-3">
							<Button
								submit
								loading={isSubmitting}
								label={t('auth.buttons.registerAndCreateOrganization')}
								block
							/>
						</div>
					</div>

					<Checkbox
						name="terms"
						control={control}
						text={
							<label htmlFor="termsOfService" className="text-sm">
								{t('auth.accept')}
								<a
									href={translatedStaticResourceResolver('terms')}
									target="_blank"
									style={{
										color: errors.terms ? 'inherit' : colors.purplishBlue,
									}}
									rel="noreferrer"
								>
									{t('auth.termsOfService')}
								</a>
							</label>
						}
						required
					/>
					<div className="text-sm">
						<p>{t('auth.registerTooltip2')} </p>
					</div>

					<div className="flex justify-content-center mt-4">
						<LanguageSwitcher />
					</div>
				</form>
			)}
		</>
	);
};

export default RegisterOrganization;
